import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import { 
  Calculator, Bitcoin, ArrowRight, CheckCircle2, 
  FileText, HelpCircle, TrendingUp, Clock, Wallet
} from 'lucide-react';
import { motion } from 'framer-motion';
import CryptoTaxCalculatorTool from '../components/tools/stage1/CryptoTaxCalculatorTool';
import { TaxPageMonetization, TaxReportEmailCapture } from '../components/TaxPageMonetization';

const BitcoinTaxCalculator = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How is Bitcoin taxed?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Bitcoin is typically taxed as property or capital gains when sold. The tax rate depends on your country and how long you held the BTC. In the US, long-term gains (>1 year) are taxed at 0-20%, while short-term gains are taxed as ordinary income (10-37%)."
        }
      },
      {
        "@type": "Question",
        "name": "Do I pay taxes on Bitcoin if I don't sell?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, simply holding Bitcoin doesn't trigger taxes in most countries. You only owe taxes when you dispose of BTC—selling for fiat, trading for another crypto, or using it to buy goods/services. Unrealized gains are not taxable."
        }
      },
      {
        "@type": "Question",
        "name": "How do I calculate my Bitcoin cost basis?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Your cost basis is the original purchase price plus fees. If you bought 0.5 BTC at $40,000 with $50 in fees, your cost basis is $20,050. For multiple purchases, use FIFO (First In First Out) or specific identification methods."
        }
      },
      {
        "@type": "Question",
        "name": "What happens if I trade Bitcoin for Ethereum?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Trading Bitcoin for another cryptocurrency is a taxable event. You must calculate the gain or loss based on the fair market value at the time of trade. For example, if your BTC cost $30,000 and is worth $50,000 when traded, you have a $20,000 taxable gain."
        }
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Bitcoin Tax Calculator (2026) - Free BTC Capital Gains Calculator"
        description="Calculate Bitcoin taxes free. Estimate capital gains tax on BTC sales, trades & transfers. Updated 2026 rates for US, UK, EU. FIFO cost basis calculator included."
        keywords="Bitcoin tax calculator, BTC tax calculator, Bitcoin capital gains, how to calculate Bitcoin tax, BTC cost basis, crypto tax on Bitcoin"
        canonical="https://toolsforgeapp.com/bitcoin-tax-calculator"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="min-h-screen py-12 px-6" data-testid="bitcoin-tax-calculator-page">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-primary transition-colors">Home</Link>
            <span>/</span>
            <Link to="/crypto-calculators" className="hover:text-primary transition-colors">Crypto Calculators</Link>
            <span>/</span>
            <span className="text-foreground">Bitcoin Tax Calculator</span>
          </nav>

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-orange-500/30 text-orange-400">
              <Bitcoin className="w-3 h-3 mr-2" />
              BTC Tax Rates Updated 2026
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-['Plus_Jakarta_Sans']">
              Bitcoin Tax Calculator
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Calculate your Bitcoin capital gains tax with precision. Whether you're selling BTC, trading it for altcoins, or using it for purchases, our free calculator estimates your tax liability based on your country's 2026 rates. Track multiple purchases with FIFO cost basis and optimize your tax position.
            </p>
          </motion.div>

          {/* Key Info Cards */}
          <div className="grid md:grid-cols-4 gap-4 mb-10">
            <div className="p-4 rounded-xl border border-orange-500/30 bg-orange-500/10">
              <div className="flex items-center gap-2 mb-2">
                <Bitcoin className="w-5 h-5 text-orange-400" />
                <span className="font-semibold">BTC Price</span>
              </div>
              <p className="text-xl font-bold text-orange-400">$67,000+</p>
              <p className="text-xs text-muted-foreground">Current market</p>
            </div>
            <div className="p-4 rounded-xl border border-green-500/30 bg-green-500/10">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <span className="font-semibold">Long-Term</span>
              </div>
              <p className="text-xl font-bold text-green-400">0-20%</p>
              <p className="text-xs text-muted-foreground">US rate (over 1yr)</p>
            </div>
            <div className="p-4 rounded-xl border border-red-500/30 bg-red-500/10">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-red-400" />
                <span className="font-semibold">Short-Term</span>
              </div>
              <p className="text-xl font-bold text-red-400">10-37%</p>
              <p className="text-xs text-muted-foreground">US rate (under 1yr)</p>
            </div>
            <div className="p-4 rounded-xl border border-blue-500/30 bg-blue-500/10">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-5 h-5 text-blue-400" />
                <span className="font-semibold">HODLers</span>
              </div>
              <p className="text-xl font-bold text-blue-400">0%</p>
              <p className="text-xs text-muted-foreground">Until you sell</p>
            </div>
          </div>

          {/* Calculator Tool */}
          <div className="mb-8">
            <CryptoTaxCalculatorTool />
          </div>

          {/* Monetization Section - After Calculator */}
          <TaxPageMonetization 
            region="bitcoin"
            complianceText="Report all BTC transactions accurately to avoid penalties"
          />

          {/* How to Calculate Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <Calculator className="w-6 h-6 text-primary" />
              How to Calculate Bitcoin Taxes
            </h2>
            
            <div className="prose prose-invert max-w-none">
              <p className="text-muted-foreground mb-4">
                Bitcoin is treated as property by most tax authorities. Every sale, trade, or use of BTC triggers a taxable event. Here's the step-by-step process:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-sm text-orange-400">1</span>
                    Track Every BTC Purchase
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Record the date, amount of BTC, price paid, and fees for every purchase. This establishes your cost basis. Example: Bought 0.25 BTC on Jan 15, 2024 for $10,500 + $25 fee = $10,525 cost basis.
                  </p>
                </div>
                
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-sm text-orange-400">2</span>
                    Identify the Disposal
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Record when you sell, trade, or spend BTC. Note the date, amount, and fair market value. Example: Sold 0.25 BTC on March 1, 2026 for $16,750.
                  </p>
                </div>
                
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-sm text-orange-400">3</span>
                    Calculate the Gain/Loss
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Subtract cost basis from sale proceeds. Using FIFO: $16,750 - $10,525 = $6,225 capital gain. For multiple purchases, match sales to oldest purchases first.
                  </p>
                </div>
                
                <div className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center text-sm text-orange-400">4</span>
                    Determine Tax Rate
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Check holding period: Over 1 year = long-term (lower rate). Under 1 year = short-term (ordinary income rate). In our example, held ~2 years = 15% long-term rate.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Example Calculation */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12 p-6 rounded-xl border border-orange-500/30 bg-orange-500/5"
          >
            <h2 className="text-xl font-bold mb-4 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-400" />
              Example: Bitcoin Tax Calculation
            </h2>
            
            <div className="bg-gray-900/50 rounded-lg p-4 font-mono text-sm">
              <p className="text-muted-foreground mb-2">// Scenario: Selling Bitcoin after 18 months</p>
              <p className="text-orange-400">Bitcoin Purchased: 0.5 BTC @ $42,000 = $21,000</p>
              <p className="text-orange-400">Exchange Fee: $50</p>
              <p className="text-orange-400">Total Cost Basis: $21,050</p>
              <p className="text-muted-foreground mt-2">// 18 months later...</p>
              <p className="text-orange-400">Sale Price: 0.5 BTC @ $68,000 = $34,000</p>
              <p className="text-orange-400">Selling Fee: $40</p>
              <p className="text-orange-400">Net Proceeds: $33,960</p>
              <p className="text-muted-foreground mt-2">// Calculation</p>
              <p className="text-white">Capital Gain: $33,960 - $21,050 = <span className="text-green-400">$12,910</span></p>
              <p className="text-white">Holding Period: 18 months = <span className="text-green-400">Long-term</span></p>
              <p className="text-white">Tax Rate (15% bracket): <span className="text-yellow-400">15%</span></p>
              <p className="text-white mt-2 text-lg">Bitcoin Tax Owed: $12,910 × 15% = <span className="text-red-400 font-bold">$1,936.50</span></p>
            </div>
          </motion.section>

          {/* Email Capture - Before FAQ */}
          <TaxReportEmailCapture region="bitcoin" />

          {/* FAQ Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-2xl font-bold mb-6 font-['Plus_Jakarta_Sans'] flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-purple-400" />
              Bitcoin Tax FAQs
            </h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "How is Bitcoin taxed?",
                  a: "Bitcoin is taxed as property or capital gains in most countries. When you sell, trade, or spend BTC, you owe tax on any gain. US long-term rates (held >1 year) are 0-20%. Short-term rates match ordinary income (10-37%). Germany offers 0% after 1 year."
                },
                {
                  q: "Do I pay taxes on Bitcoin if I don't sell?",
                  a: "No. Simply holding Bitcoin doesn't trigger taxes. You only owe tax when you 'dispose' of BTC—selling for fiat, trading for ETH or other crypto, using it to buy goods, or gifting it. Unrealized gains are not taxable."
                },
                {
                  q: "How do I calculate my Bitcoin cost basis?",
                  a: "Add your purchase price plus all fees. If you bought 0.1 BTC for $6,700 with a $15 fee, your cost basis is $6,715. For multiple purchases, use FIFO (First In First Out), LIFO, or specific identification—FIFO is default in most countries."
                },
                {
                  q: "What happens if I trade Bitcoin for Ethereum?",
                  a: "It's a taxable event. Calculate gain/loss based on BTC's fair market value when traded. If your BTC cost $30,000 and is worth $50,000 when you swap for ETH, you have a $20,000 taxable gain—even though you received crypto, not cash."
                },
                {
                  q: "Can I reduce my Bitcoin taxes?",
                  a: "Yes. Hold for over 1 year for lower long-term rates. Harvest losses by selling underperforming crypto. Use tax-advantaged accounts where available. In Germany, hold over 1 year for 0% tax. Always consult a tax professional."
                }
              ].map((faq, index) => (
                <div key={index} className="p-5 rounded-xl border border-border bg-card">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-orange-400" />
                    {faq.q}
                  </h3>
                  <p className="text-sm text-muted-foreground">{faq.a}</p>
                </div>
              ))}
            </div>
          </motion.section>

          {/* Related Tools */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8"
          >
            <h2 className="text-xl font-semibold mb-4 font-['Plus_Jakarta_Sans']">More Bitcoin Tools</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: 'Bitcoin Profit Calculator', slug: 'bitcoin-profit-calculator' },
                { name: 'Bitcoin ROI Calculator', slug: 'bitcoin-roi-calculator' },
                { name: 'Crypto Tax Calculator', slug: 'crypto-tax-calculator' },
                { name: 'All Crypto Calculators', slug: '../crypto-calculators', isHub: true }
              ].map(tool => (
                <Link
                  key={tool.slug}
                  to={tool.isHub ? '/crypto-calculators' : `/tools/${tool.slug}`}
                  className="p-3 rounded-lg border border-border bg-card hover:border-orange-500/50 transition-all text-sm font-medium flex items-center justify-between"
                >
                  {tool.name}
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </motion.section>
        </div>
      </div>
    </>
  );
};

export default BitcoinTaxCalculator;
